import {
  IApprovalRequest,
  ICountSubmissionRequest,
  IGetSessionsRequest,
} from '../../types/api/request/stocktakerequest';
import {
  IBinContentsResponse,
  ILocationsResponse,
  IMovementCheckResponse,
  IPostResultResponse,
  IProductSearchResponse,
  IReviewResponse,
  ISessionDetailResponse,
  ISessionSummary,
  ISessionsResponse,
} from '../../types/api/response/stocktakeresponse';
import axiosClient from '../axiosClient';
import { StocktakeEndpoints } from '../endpoints/stocktake';

export const StocktakeService = {
  async getLocations(): Promise<ILocationsResponse> {
    const res = await axiosClient.get<ILocationsResponse>(StocktakeEndpoints.GET_LOCATIONS);
    return res.data;
  },

  async openBin(location: string, binGuid: string): Promise<IBinContentsResponse> {
    const qry = `?location=${encodeURIComponent(location)}&bin_guid=${encodeURIComponent(binGuid)}`;
    // The first open of a lot is a cold ~10s Cin7 availability pull — allow up to a minute.
    const res = await axiosClient.get<IBinContentsResponse>(`${StocktakeEndpoints.GET_BIN}${qry}`, {
      timeout: 60000,
    });
    return res.data;
  },

  async searchProducts(q: string): Promise<IProductSearchResponse> {
    const res = await axiosClient.get<IProductSearchResponse>(
      `${StocktakeEndpoints.SEARCH_PRODUCTS}?q=${encodeURIComponent(q)}`,
    );
    return res.data;
  },

  async review(ref: number, payload: ICountSubmissionRequest): Promise<IReviewResponse> {
    const url = StocktakeEndpoints.REVIEW_SESSION.replace(':ref', String(ref));
    const res = await axiosClient.post<IReviewResponse>(url, payload);
    return res.data;
  },

  async post(ref: number, payload: ICountSubmissionRequest): Promise<IPostResultResponse> {
    const url = StocktakeEndpoints.POST_SESSION.replace(':ref', String(ref));
    // A post can make many throttled Cin7 calls (stocktake + each addition). Allow up to 5 min —
    // well under the admin's 15-min read timeout — so the UI never gives up on a post still in flight.
    const res = await axiosClient.post<IPostResultResponse>(url, payload, { timeout: 300000 });
    return res.data;
  },

  async getSessionDetail(ref: number): Promise<ISessionDetailResponse> {
    const url = StocktakeEndpoints.SESSION_DETAIL.replace(':ref', String(ref));
    const res = await axiosClient.get<ISessionDetailResponse>(url);
    return res.data;
  },

  async movementCheck(ref: number): Promise<IMovementCheckResponse> {
    const url = StocktakeEndpoints.MOVEMENT_CHECK.replace(':ref', String(ref));
    // Re-reads current Cin7 stock (a fresh availability pull) — allow up to a minute.
    const res = await axiosClient.get<IMovementCheckResponse>(url, { timeout: 60000 });
    return res.data;
  },

  async approve(ref: number, payload: IApprovalRequest): Promise<ISessionSummary> {
    const url = StocktakeEndpoints.APPROVE_SESSION.replace(':ref', String(ref));
    // Approve replays the count to Cin7 (stocktake + adjustments) — allow up to 5 min.
    const res = await axiosClient.post<ISessionSummary>(url, payload, { timeout: 300000 });
    return res.data;
  },

  async reject(ref: number, payload: IApprovalRequest): Promise<ISessionSummary> {
    const url = StocktakeEndpoints.REJECT_SESSION.replace(':ref', String(ref));
    const res = await axiosClient.post<ISessionSummary>(url, payload);
    return res.data;
  },

  async getCapabilities(): Promise<{ can_adjust: boolean; can_self_approve: boolean }> {
    const res = await axiosClient.get<{ can_adjust: boolean; can_self_approve: boolean }>(
      StocktakeEndpoints.CAPABILITIES,
    );
    return res.data;
  },

  async getSessions(params?: IGetSessionsRequest): Promise<ISessionsResponse> {
    const q: string[] = [];
    if (params?.status) q.push(`status=${encodeURIComponent(params.status)}`);
    if (params?.from) q.push(`from=${params.from}`);
    if (params?.to) q.push(`to=${params.to}`);
    if (params?.page != null) q.push(`page=${params.page}`);
    if (params?.size != null) q.push(`size=${params.size}`);
    const qry = q.length ? `?${q.join('&')}` : '';
    const res = await axiosClient.get<ISessionsResponse>(
      `${StocktakeEndpoints.GET_SESSIONS}${qry}`,
    );
    return res.data;
  },
};
